import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  // Switch,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  // TextField,
  // FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  // Visibility as ViewIcon,
  // Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import FormSection from "./FormSection.jsx";
import FormSelect from "./FormSelect.jsx";
import {
  useLazyGetEquipmentTypesQuery,
  useLazyGetManufacturersQuery,
  useLazyGetEquipmentModelsQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';

const EquipmentStep = ({ formData = {}, updateFormData }) => {
  const [fetchEquipmentTypes] = useLazyGetEquipmentTypesQuery();
  const [fetchManufacturers] = useLazyGetManufacturersQuery();
  const [fetchEquipmentModels] = useLazyGetEquipmentModelsQuery();

  const equipmentTypeSelect = useLazySelectOptions(fetchEquipmentTypes, {
    mapResult: (response) => {
      const data = extractList(response);
      if (!Array.isArray(data)) return [];
      return data.map((item) => ({ value: item?.id, label: item?.name }));
    },
  });

  const manufacturerSelect = useLazySelectOptions(fetchManufacturers, {
    mapResult: (response) => {
      const data = extractList(response);
      if (!Array.isArray(data)) return [];
      return data.map((item) => ({ value: item?.id, label: item?.name }));
    },
  });

  const equipmentTypeOptions = equipmentTypeSelect.options;
  const manufacturerOptions = manufacturerSelect.options;
  const [equipmentModelOptions, setEquipmentModelOptions] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [error, setError] = useState(null);

  // const [deploymentCompleted, setDeploymentCompleted] = useState(false);
  const [equipmentType, setEquipmentType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [equipmentModel, setEquipmentModel] = useState("");
  const [equipmentList, setEquipmentList] = useState(formData.equipment || []);
  const [notesList, setNotesList] = useState(formData.equipment_notes || []);
  // const [newNote, setNewNote] = useState("");
  const isInitialized = useRef(false);

  // console.log(equipmentList, 'yeh hai mera pyara bhai222');

  // Memoize updateFormData to avoid unnecessary re-renders
  const memoizedUpdateFormData = useCallback(() => {
    if (updateFormData) {
      updateFormData({
        equipment: equipmentList,
        equipment_notes: notesList,
      });
    }
  }, [equipmentList, notesList, updateFormData]);

  // Update formData when equipmentList or notesList changes
  useEffect(() => {
    const equipmentChanged = equipmentList.length !== formData.equipment?.length;
    const notesChanged = notesList.length !== formData.equipment_notes?.length;

    if (equipmentChanged || notesChanged) {
      memoizedUpdateFormData();
    }
  }, [memoizedUpdateFormData, equipmentList, notesList, formData.equipment, formData.equipment_notes]);

  // Sync local state with formData prop when navigating between steps
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && !isInitialized.current) {
      setEquipmentList(formData.equipment || []);
      setNotesList(formData.equipment_notes || []);
      isInitialized.current = true;
    }
  }, [formData]);

  useEffect(() => {
    if (equipmentType && manufacturer) {
      setModelsLoading(true);
      fetchEquipmentModels({ equipment_type_id: equipmentType, manufacturer_id: manufacturer })
        .unwrap()
        .then((response) => {
          const data = extractList(response);
          if (Array.isArray(data)) {
            setEquipmentModelOptions(data.map((item) => ({ value: item?.id, label: item?.name })));
          } else {
            setEquipmentModelOptions([]);
          }
        })
        .catch((err) => {
          console.error("Error fetching equipment models:", err);
          setError("Failed to load equipment models.");
        })
        .finally(() => setModelsLoading(false));
    } else if (equipmentModelOptions.length > 0) {
      setEquipmentModelOptions([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [equipmentType, manufacturer, fetchEquipmentModels]);

  const handleAddEquipment = useCallback(() => {
    if (equipmentType && manufacturer && equipmentModel) {
      const selectedModel = equipmentModelOptions.find(opt => opt.value === equipmentModel);
      const selectedType = equipmentTypeOptions.find(opt => opt.value === equipmentType);
      const selectedManufacturer = manufacturerOptions.find(opt => opt.value === manufacturer);

      if (!selectedModel || !selectedType || !selectedManufacturer) return;

      const newEquipment = {
        id: Date.now(),
        equipment_type_id: equipmentType,
        manufacturer_id: manufacturer,
        equipment_model_id: equipmentModel,
        activated: true,
        // Store labels for display
        equipment_model_name: selectedModel.label,
        equipment_type_name: selectedType.label,
        manufacturer_name: selectedManufacturer.label,
      };

      const isDuplicate = equipmentList.some(
        (eq) =>
          eq.equipment_type_id === newEquipment.equipment_type_id &&
          eq.manufacturer_id === newEquipment.manufacturer_id &&
          eq.equipment_model_id === newEquipment.equipment_model_id
      );

      if (!isDuplicate) {
        setEquipmentList((prev) => [...prev, newEquipment]);
        setEquipmentType("");
        setManufacturer("");
        setEquipmentModel("");
      } else {
        setError("This equipment combination is already added.");
      }
    }
  }, [equipmentType, manufacturer, equipmentModel, equipmentList, equipmentTypeOptions, manufacturerOptions, equipmentModelOptions]);

  const handleRemoveEquipment = useCallback((id) => {
    setEquipmentList((prev) => prev.filter((equipment) => equipment.id !== id));
  }, []);

  const handleEquipmentTypeChange = useCallback(
    (event) => {
      const value = event?.target?.value || "";
      if (value !== equipmentType) {
        setEquipmentType(value);
        setManufacturer("");
        setEquipmentModel("");
      }
    },
    [equipmentType]
  );

  const handleManufacturerChange = useCallback(
    (event) => {
      const value = event?.target?.value || "";
      if (value !== manufacturer) {
        setManufacturer(value);
        setEquipmentModel("");
      }
    },
    [manufacturer]
  );

  const handleEquipmentModelChange = useCallback(
    (event) => {
      setEquipmentModel(event?.target?.value || "");
    },
    []
  );

  const handleClear = useCallback(() => {
    setEquipmentList([]);
    setNotesList([]);
    setEquipmentType("");
    setManufacturer("");
    setEquipmentModel("");
    setError(null);
  }, []);



  return (
    <Box sx={{ p: 3 }}>
      <FormSection title="Equipment" bgColor="blue.50">
        {error && <Typography color="error">{error}</Typography>}

        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 2,
            p: 3,
            mb: 3,
            bgcolor: "white",
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, color: "grey.700" }}>
            Equipment Details
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <FormSelect
              label="Equipment Type"
              value={equipmentType || ""}
              onChange={handleEquipmentTypeChange}
              options={equipmentTypeOptions}
              placeholder="Select Equipment Type"
              searchable={true}
              loading={equipmentTypeSelect.loading}
              onOpen={() => {
                equipmentTypeSelect.load().catch((err) => {
                  console.error("Error fetching equipment types:", err);
                  setError("Failed to load equipment types.");
                });
              }}
            />
            <FormSelect
              label="Manufacturer"
              value={manufacturer || ""}
              onChange={handleManufacturerChange}
              options={manufacturerOptions}
              placeholder="Select Manufacturer"
              searchable={true}
              disabled={!equipmentType}
              loading={manufacturerSelect.loading}
              onOpen={() => {
                manufacturerSelect.load().catch((err) => {
                  console.error("Error fetching manufacturers:", err);
                  setError("Failed to load manufacturers.");
                });
              }}
            />
            <FormSelect
              label="Equipment Model"
              value={equipmentModel || ""}
              onChange={handleEquipmentModelChange}
              options={equipmentModelOptions}
              placeholder="Select Model"
              searchable={true}
              disabled={!equipmentType || !manufacturer}
              loading={modelsLoading}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddEquipment}
              disabled={!equipmentType || !manufacturer || !equipmentModel || modelsLoading}
              sx={{
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              Add Equipment
            </Button>
            <Button
              variant="contained"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{
                bgcolor: "orange.main",
                "&:hover": { bgcolor: "orange.dark" },
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                {/* <TableCell>Id Equipment</TableCell> */}
                <TableCell>Equipment Model</TableCell>
                <TableCell>Equipment Type</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Activated/Deactivated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipmentList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      There's no records to display
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                equipmentList.map((equipment) => (
                  <TableRow key={equipment.id}>

                    {/* <TableCell>{equipment.id}</TableCell> */}
                    <TableCell>
                    <TableCell>{equipment.equipment_model_name || "N/A"}</TableCell>
                    </TableCell>
                    <TableCell>

                      {equipmentTypeOptions.find((opt) => opt.value === equipment.equipment_type_id)?.label ||
                        equipment.equipment_type?.name ||
                        "N/A"}
                    </TableCell>
                    <TableCell>
                      {manufacturerOptions.find((opt) => opt.value === equipment.manufacturer_id)?.label ||
                        equipment.manufacturer?.name ||
                        "N/A"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={equipment.activated ? "Activated" : "Deactivated"}
                        color={equipment.activated ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {/* <IconButton size="small" color="primary" onClick={() => console.log("View", equipment)}>
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" color="primary" onClick={() => console.log("Edit", equipment)}>
                          <EditIcon />
                        </IconButton> */}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveEquipment(equipment.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* <FormControlLabel
            control={
              <Switch
                checked={deploymentCompleted}
                onChange={(e) => setDeploymentCompleted(e.target.checked)}
                color="primary"
              />
            }
            label="Deployment Completed"
          /> */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEquipment}
            sx={{
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
            }}
            disabled={!equipmentType || !manufacturer || !equipmentModel || modelsLoading}
          >
            Add Equipment
          </Button>
        </Box>
      </FormSection>

      {/* <FormSection title="Notes" bgColor="grey.50">
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              display: "inline-block",
              bgcolor: "primary.main",
              color: "white",
              px: 2,
              py: 1,
              borderRadius: 1,
              mb: 2,
            }}
          >
            Equipment Notes
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            name="equipment[note]"
            placeholder="Enter note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNote}
            sx={{
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            Add Note
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell>Note</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notesList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      There's no records to display
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                notesList.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell>{note.note}</TableCell>
                    <TableCell>{note.date}</TableCell>
                    <TableCell>{note.userName}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton size="small" color="primary">
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormSection> */}
    </Box>
  );
};

export default EquipmentStep;